import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Phone, 
  MapPin, 
  Home, 
  Check, 
  Search, 
  ExternalLink, 
  Loader2,
  Globe,
  MessageCircle,
  Square,
  CheckSquare,
  History,
  Info,
  Target,
  X
} from 'lucide-react';
import NotesModal from '../NotesModal/NotesModal';
import './AgentCard.css';

// Normalize phone numbers: remove all non-digits, keep last 10 digits
const normalizePhone = (phone) => {
  if (!phone) return '';
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.slice(-10);
};

// Lead status options
const leadStatusOptions = [
  { value: null, label: "No Status", color: "#9ca3af" },
  { value: "lead", label: "Lead", color: "#3b82f6" },
  { value: "prospect", label: "Prospect", color: "#f59e0b" },
  { value: "interested", label: "Interested", color: "#06b6d4" },
  { value: "not-interested", label: "Not Interested", color: "#f97316" },
  { value: "negotiation", label: "Negotiation", color: "#8b5cf6" },
  { value: "done", label: "Done", color: "#10b981" },
  { value: "lost", label: "Lost", color: "#ef4444" }
];

const getInitials = (name) => {
  if (!name) return '??';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Format timestamp to readable format
const formatCallTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMinutes = Math.floor((now - date) / 60000);
  
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const AgentCard = ({ agent, onCall, onUpdateStatus, callingPhoneId, selectedPhones, onTogglePhone, phoneCallStatuses, phoneCallSuccessful, phoneCallTimestamps }) => {
  const [notes, setNotes] = useState(agent.notes || '');
  const [status, setStatus] = useState(agent.leadStatus);
  const [saving, setSaving] = useState(false);
  const [showAllProperties, setShowAllProperties] = useState(false);
  const [confirmPhone, setConfirmPhone] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [modalView, setModalView] = useState('notes');

  useEffect(() => {
    setNotes(agent.notes || '');
    setStatus(agent.leadStatus);
  }, [agent.notes, agent.leadStatus]);

  const statusClass = status ? `status-${status}` : 'status-none';
  const statusLabel = leadStatusOptions.find(opt => opt.value === status)?.label || 'No Status';
  
  // Lazy property loading - show first 5 by default
  const propertiesToShow = showAllProperties ? agent.properties : agent.properties.slice(0, 5);
  const hasMoreProperties = agent.properties.length > 5;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdateStatus(agent.id, status, notes);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      // Clear status and notes in database (skip validation for reset)
      await onUpdateStatus(agent.id, null, '', true);
      // Reset local state
      setNotes('');
      setStatus(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="agent-card">
      {/* Header */}
      <div className="agent-card-header">
        <div className="agent-info">
          <div className="agent-avatar">
            {getInitials(agent.name)}
          </div>
          <div className="agent-details">
            <h3>{agent.name}</h3>
            {agent.officeName && (
              <div className="office-badge">
                <Building2 />
                <span>{agent.officeName}</span>
              </div>
            )}
          </div>
        </div>
        <div className="agent-status">
          <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
        </div>
      </div>

      {/* Phone Numbers */}
      <div className="phone-section">
        <div className="section-title">
          <Phone />
          Phone Numbers ({agent.phones.length})
        </div>
        <div className="phone-list">
          {agent.phones.map((phone) => {
            const phoneKey = `${agent.id}::${phone.number}`;
            const isSelected = selectedPhones?.has(phoneKey);
            const callStatus = phoneCallStatuses?.[phone.number];
            const callSuccessful = phoneCallSuccessful?.[phone.number];
            const callTimestamps = phoneCallTimestamps?.[phone.number];
            const callTime = callTimestamps?.updated_at || callTimestamps?.created_at;

            return (
              <div key={phone.id} className={`phone-item ${isSelected ? 'phone-selected' : ''}`}>
                <button
                  className="phone-select-btn"
                  onClick={() => onTogglePhone && onTogglePhone(agent, phone)}
                  title={isSelected ? 'Remove from batch' : 'Add to batch'}
                >
                  {isSelected ? <CheckSquare className="checked" /> : <Square />}
                </button>
                <div className="phone-info">
                  <Phone />
                  <div className="phone-number-col">
                    <span className="phone-number">{phone.number}</span>
                    <div className="phone-status-badges">
                      {callStatus && (
                        <div className="phone-call-status-group">
                          <span className="phone-call-status">
                            <Info size={16} />
                            {callStatus}
                          </span>
                          {callTime && (
                            <span className="phone-call-time" title={new Date(callTime).toLocaleString()}>
                              <History size={14} />
                              {formatCallTime(callTime)}
                            </span>
                          )}
                        </div>
                      )}
                      {callSuccessful && (
                        <span className="phone-call-successful">
                          <Target size={16} />
                          {callSuccessful}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button 
                  className="call-btn whatsapp-btn"
                  onClick={() => setConfirmPhone(phone)}
                  disabled={callingPhoneId === phone.id}
                  title="Send via WhatsApp"
                >
                  {callingPhoneId === phone.id ? (
                    <Loader2 className="spin" />
                  ) : (
                    <MessageCircle />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Properties */}
      <div className="properties-section">
        <div className="section-title">
          <Home />
          Properties ({agent.properties.length})
        </div>
        <div className="properties-list">
          {propertiesToShow.map((property) => (
            <div key={property.id} className="property-item">
              <div className="property-main">
                <div className="property-badges">
                  <span className="property-type-badge">{property.type}</span>
                  {property.transactionType && (
                    <span className="property-transaction-badge">{property.transactionType}</span>
                  )}
                </div>
                <div className="property-location">
                  <MapPin />
                  <span>
                    {property.city}{property.district ? `, ${property.district}` : ''}{property.neighbourhood ? `, ${property.neighbourhood}` : ''}
                  </span>
                </div>
              </div>
              <div className="property-right">
                {property.website && (
                  <span className="property-portal-badge">
                    <Globe />
                    {property.website}
                  </span>
                )}
                <span className="property-price">{property.priceFormatted || `${property.price?.toLocaleString()} TL`}</span>
                {property.url && (
                  <a href={property.url} target="_blank" rel="noopener noreferrer" className="property-link">
                    <ExternalLink />
                    View
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        {hasMoreProperties && (
          <button 
            className="show-more-btn"
            onClick={() => setShowAllProperties(!showAllProperties)}
          >
            {showAllProperties 
              ? `Show less` 
              : `Show all ${agent.properties.length} properties`
            }
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="actions-section">
        <div className="status-notes-container">
          <div className="status-group">
            <label className="input-label">
              <Search />
              Lead Status
            </label>
            <select 
              className="status-select"
              value={status || ''} 
              onChange={(e) => setStatus(e.target.value || null)}
            >
              {leadStatusOptions.map((opt) => (
                <option key={opt.value || 'none'} value={opt.value || ''}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="notes-group">
            <div className="notes-label-row">
              <label className="input-label">
                Notes
              </label>
              <button 
                className="history-toggle-btn"
                onClick={() => {
                  setModalView('history');
                  setShowNotesModal(true);
                }}
                title="View history"
              >
                <History size={14} />
                History
              </button>
            </div>
            <textarea
              className="notes-input"
              placeholder="Add notes about this agent..."
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onClick={() => {
                if (notes) {
                  setModalView('notes');
                  setShowNotesModal(true);
                }
              }}
              style={{ cursor: notes ? 'pointer' : 'text' }}
            />
          </div>
        </div>

        <div className="action-buttons">
          <button className="action-btn secondary" onClick={handleReset} disabled={saving}>
            <X />
            Reset
          </button>
          <button className="action-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 /> : <Check />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        <div className="last-contacted">
          {agent.lastUpdatedAt 
            ? `Last updated: ${new Date(agent.lastUpdatedAt).toLocaleDateString()}` 
            : 'Not updated yet'}
        </div>
      </div>
      {/* Full Notes Modal */}
      <NotesModal 
        isOpen={showNotesModal}
        notes={notes}
        onClose={() => setShowNotesModal(false)}
        title="Full Notes"
        phone={agent.phones?.[0]?.number || null}
        leadStatus={status}
        view={modalView}
      />

      {/* Single-call confirmation modal */}
      {confirmPhone && (
        <div className="confirm-overlay" onClick={() => setConfirmPhone(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <p className="confirm-title">Send WhatsApp Call?</p>
            <p className="confirm-number">{confirmPhone.number}</p>
            <div className="confirm-actions">
              <button className="confirm-btn cancel" onClick={() => setConfirmPhone(null)}>Cancel</button>
              <button
                className="confirm-btn ok"
                onClick={() => { onCall(agent, confirmPhone); setConfirmPhone(null); }}
              >
                <MessageCircle size={14} /> Call
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentCard;
